import L from "leaflet";

import type { GisPreviewLayer } from "./types";

/** Files the drive opens on a QGIS-rendered map instead of downloading them. */
export const GIS_PREVIEW_EXTENSIONS = [".gpkg"];

export function isGisPreviewable(name: string): boolean {
  const lower = name.toLowerCase();
  return GIS_PREVIEW_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/** EPSG:3857 [minx, miny, maxx, maxy] → Leaflet bounds. */
export function extentToBounds(extent: [number, number, number, number]): L.LatLngBounds {
  const [minx, miny, maxx, maxy] = extent;
  return L.latLngBounds(
    L.CRS.EPSG3857.unproject(L.point(minx, miny)),
    L.CRS.EPSG3857.unproject(L.point(maxx, maxy)),
  );
}

export interface FeatureInfoRequest {
  /** WMS layer names to query, bottom → top. */
  layers: string[];
  /** Current view in EPSG:3857: [minx, miny, maxx, maxy]. */
  bbox: [number, number, number, number];
  width: number;
  height: number;
  /** Clicked pixel inside the view. */
  x: number;
  y: number;
}

/**
 * WMS GetFeatureInfo on the preview's signed URL (the signature covers only map+expires,
 * so any request on that project passes). Tolerances are in pixels — a click within a few
 * pixels of a point or a line still hits it.
 */
export function featureInfoUrl(wmsUrl: string, req: FeatureInfoRequest): string {
  const params = new URLSearchParams({
    SERVICE: "WMS",
    VERSION: "1.3.0",
    REQUEST: "GetFeatureInfo",
    LAYERS: req.layers.join(","),
    QUERY_LAYERS: req.layers.join(","),
    STYLES: "",
    CRS: "EPSG:3857",
    BBOX: req.bbox.join(","),
    WIDTH: String(req.width),
    HEIGHT: String(req.height),
    I: String(req.x),
    J: String(req.y),
    INFO_FORMAT: "application/json",
    FEATURE_COUNT: "10",
    FI_POINT_TOLERANCE: "10",
    FI_LINE_TOLERANCE: "6",
    FI_POLYGON_TOLERANCE: "2",
    WITH_GEOMETRY: "false",
  });
  return `${wmsUrl}${wmsUrl.includes("?") ? "&" : "?"}${params.toString()}`;
}

export interface FeatureInfoHit {
  layerTitle: string;
  attributes: [string, string][];
}

export function formatAttribute(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "tak" : "nie";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/**
 * QGIS Server's JSON GetFeatureInfo: a FeatureCollection whose feature ids are
 * "<layer name>.<fid>". Hits come back topmost layer first, as on the map.
 */
export function parseFeatureInfo(json: unknown, layers: GisPreviewLayer[]): FeatureInfoHit[] {
  const features = (json as { features?: unknown[] } | null)?.features;
  if (!Array.isArray(features)) return [];
  const titleOf = new Map(layers.map((l) => [l.name, l.title]));
  const order = new Map(layers.map((l, i) => [l.name, i]));
  return features
    .map((f) => {
      const feature = f as { id?: unknown; properties?: Record<string, unknown> | null };
      const layerName = String(feature.id ?? "").split(".")[0];
      return {
        rank: order.get(layerName) ?? -1,
        hit: {
          layerTitle: titleOf.get(layerName) ?? layerName,
          attributes: Object.entries(feature.properties ?? {}).map(
            ([k, v]) => [k, formatAttribute(v)] as [string, string],
          ),
        },
      };
    })
    .sort((a, b) => b.rank - a.rank)
    .map((x) => x.hit);
}

/** The API's refusal reason ({errors: {detail}} or {errors: [..]}), or a generic one. */
export function previewErrorMessage(err: unknown): string {
  const errors = (err as { response?: { data?: { errors?: unknown } } })?.response?.data?.errors;
  if (Array.isArray(errors) && errors.length) return errors.map(String).join(" ");
  const detail = (errors as { detail?: unknown } | undefined)?.detail;
  if (typeof detail === "string") return detail;
  return "Nie udało się otworzyć podglądu mapy.";
}
